import { Fragment, type JSX, type ReactNode } from 'react';

/**
 * Markdown a elementos de React, sin dependencias y sin HTML crudo.
 *
 * POR QUÉ NO SE USA UNA LIBRERÍA
 * Lo habitual sería `marked` + `DOMPurify` y un `dangerouslySetInnerHTML`. Se descartó por
 * dos razones que van juntas:
 *
 *  1. Ese camino EJECUTA HTML. El contenido viene de repositorios propios, pero «propio»
 *     no es «confiable para siempre»: basta que alguien pegue en un README un fragmento
 *     copiado de internet. Aquí no se genera HTML en ningún punto —se construyen nodos de
 *     React— así que una etiqueta `<script>` en un documento se ve como texto, que es lo
 *     que es. La inyección no se mitiga: no existe.
 *
 *  2. Este repositorio tiene una puerta de `npm audit` en su CI. Dos dependencias más son
 *     dos avisos futuros que alguien tendrá que atender un martes por la mañana.
 *
 * QUÉ CUBRE, Y QUÉ NO
 * Encabezados, párrafos, listas, bloques de código, citas, tablas simples, reglas, y en
 * línea: negrita, cursiva, código y enlaces. No cubre HTML incrustado, notas al pie ni
 * listas de tareas anidadas. Es documentación técnica leída en una pantalla, no un motor
 * de publicación; cuando algo no se entiende bien, el enlace «ver en GitHub» está al lado.
 */

/** Trocea una línea en negritas, cursivas, código y enlaces. Sin recursión: no hace falta. */
function enLinea(texto: string, clave: string): ReactNode[] {
  const nodos: ReactNode[] = [];
  // El orden importa: el código va primero para que `**` dentro de un `código` no se
  // interprete como negrita.
  const patron = /(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)/g;
  let ultimo = 0;
  let m: RegExpExecArray | null;
  let n = 0;

  while ((m = patron.exec(texto)) !== null) {
    if (m.index > ultimo) nodos.push(texto.slice(ultimo, m.index));
    const t = m[0];
    const k = `${clave}-${n++}`;

    if (t.startsWith('`')) {
      nodos.push(
        <code key={k} className="rounded bg-[var(--color-surface-muted)] px-1 py-0.5 text-[0.9em]">
          {t.slice(1, -1)}
        </code>,
      );
    } else if (t.startsWith('[')) {
      const corte = t.indexOf('](');
      const etiqueta = t.slice(1, corte);
      const destino = t.slice(corte + 2, -1);
      // Solo http(s) y anclas. Un `javascript:` en un enlace es la única vía de ejecución
      // que quedaría en todo este componente, así que se cierra explícitamente.
      const seguro = /^(https?:\/\/|#|\/)/i.test(destino);
      nodos.push(
        seguro ? (
          <a key={k} href={destino} target="_blank" rel="noreferrer" className="underline">
            {etiqueta}
          </a>
        ) : (
          <Fragment key={k}>{etiqueta}</Fragment>
        ),
      );
    } else if (t.startsWith('**')) {
      nodos.push(<strong key={k}>{t.slice(2, -2)}</strong>);
    } else {
      nodos.push(<em key={k}>{t.slice(1, -1)}</em>);
    }
    ultimo = m.index + t.length;
  }
  if (ultimo < texto.length) nodos.push(texto.slice(ultimo));
  return nodos;
}

export function Markdown({ texto }: { texto: string }): JSX.Element {
  const lineas = texto.split('\n');
  const bloques: ReactNode[] = [];
  let i = 0;
  let n = 0;

  const parrafo: string[] = [];
  const cerrarParrafo = (): void => {
    if (parrafo.length === 0) return;
    const contenido = parrafo.join(' ');
    parrafo.length = 0;
    bloques.push(
      <p key={`p${n++}`} className="mb-3 leading-relaxed">
        {enLinea(contenido, `p${n}`)}
      </p>,
    );
  };

  while (i < lineas.length) {
    const linea = lineas[i] ?? '';

    // Bloque de código: se copia tal cual hasta el cierre.
    if (/^\s*```/.test(linea)) {
      cerrarParrafo();
      const idioma = linea.replace(/^\s*```/, '').trim();
      const cuerpo: string[] = [];
      i += 1;
      while (i < lineas.length && !/^\s*```/.test(lineas[i] ?? '')) {
        cuerpo.push(lineas[i] ?? '');
        i += 1;
      }
      i += 1;
      bloques.push(
        <pre
          key={`c${n++}`}
          className="mb-3 overflow-x-auto rounded bg-[var(--color-surface-muted)] p-3 text-[12px] leading-relaxed"
        >
          <code>{cuerpo.join('\n')}</code>
          {idioma && <span className="sr-only">{idioma}</span>}
        </pre>,
      );
      continue;
    }

    const enc = /^(#{1,6})\s+(.*)$/.exec(linea);
    if (enc) {
      cerrarParrafo();
      const nivel = enc[1]?.length ?? 1;
      const tam = [
        'text-[19px]',
        'text-[17px]',
        'text-[15px]',
        'text-[14px]',
        'text-[13px]',
        'text-[13px]',
      ][nivel - 1];
      bloques.push(
        <p key={`h${n++}`} className={`mb-2 mt-5 font-semibold ${tam}`}>
          {enLinea(enc[2] ?? '', `h${n}`)}
        </p>,
      );
      i += 1;
      continue;
    }

    if (/^\s*([-*_])\s*\1\s*\1[\s-*_]*$/.test(linea)) {
      cerrarParrafo();
      bloques.push(<hr key={`r${n++}`} className="my-4 border-[var(--color-border-subtle)]" />);
      i += 1;
      continue;
    }

    if (/^\s*>\s?/.test(linea)) {
      cerrarParrafo();
      const cita: string[] = [];
      while (i < lineas.length && /^\s*>\s?/.test(lineas[i] ?? '')) {
        cita.push((lineas[i] ?? '').replace(/^\s*>\s?/, ''));
        i += 1;
      }
      bloques.push(
        <blockquote
          key={`q${n++}`}
          className="mb-3 border-l-2 border-[var(--color-border-subtle)] pl-3 text-[var(--color-ink-muted)]"
        >
          {enLinea(cita.join(' '), `q${n}`)}
        </blockquote>,
      );
      continue;
    }

    // Tabla: cabecera, separador y filas. Se acepta solo la forma simple con tuberías.
    if (/^\s*\|/.test(linea) && /^\s*\|?[\s:-]*\|[\s:|-]*$/.test(lineas[i + 1] ?? '')) {
      cerrarParrafo();
      const celdas = (l: string) =>
        l
          .replace(/^\s*\|/, '')
          .replace(/\|\s*$/, '')
          .split('|')
          .map((c) => c.trim());
      const cabecera = celdas(linea);
      i += 2;
      const filas: string[][] = [];
      while (i < lineas.length && /^\s*\|/.test(lineas[i] ?? '')) {
        filas.push(celdas(lineas[i] ?? ''));
        i += 1;
      }
      bloques.push(
        <div key={`t${n++}`} className="mb-3 overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                {cabecera.map((c, x) => (
                  <th
                    key={x}
                    className="border-b border-[var(--color-border-subtle)] px-2 py-1 text-left font-medium"
                  >
                    {enLinea(c, `th${n}-${x}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f, y) => (
                <tr key={y}>
                  {f.map((c, x) => (
                    <td
                      key={x}
                      className="border-b border-[var(--color-border-subtle)] px-2 py-1 align-top"
                    >
                      {enLinea(c, `td${n}-${y}-${x}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const lista = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(linea);
    if (lista) {
      cerrarParrafo();
      const ordenada = /\d/.test(lista[2] ?? '');
      const puntos: ReactNode[] = [];
      let p = 0;
      while (i < lineas.length) {
        const m2 = /^(\s*)([-*+]|\d+\.)\s+(.*)$/.exec(lineas[i] ?? '');
        if (!m2) break;
        const sangria = (m2[1] ?? '').length;
        puntos.push(
          <li key={p++} className={sangria >= 2 ? 'ml-5' : ''}>
            {enLinea(m2[3] ?? '', `li${n}-${p}`)}
          </li>,
        );
        i += 1;
      }
      const Etiqueta = ordenada ? 'ol' : 'ul';
      bloques.push(
        <Etiqueta
          key={`l${n++}`}
          className={`mb-3 ml-5 space-y-1 ${ordenada ? 'list-decimal' : 'list-disc'}`}
        >
          {puntos}
        </Etiqueta>,
      );
      continue;
    }

    if (linea.trim() === '') {
      cerrarParrafo();
      i += 1;
      continue;
    }

    parrafo.push(linea.trim());
    i += 1;
  }
  cerrarParrafo();

  return <div className="text-[13px]">{bloques}</div>;
}
